/**
 * Capability map: Keycloak client role names → UI buckets used by menus / routes / drawer.
 *
 * Add new industrial roles here only. Do not hardcode role names in ResponsiveDrawer,
 * MenuConfig, or AppRoutes — they should call helpers from this file.
 *
 * Viewer gets app shell + read-only HOMS (menus/routes); mutate page keys stay off.
 * Unmapped roles (e.g. User) intentionally grant no app shell.
 */

export const UI_BUCKET = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  AGENT: 'Agent',
  VIEWER: 'Viewer',
};

const BUCKET_PRIORITY = [
  UI_BUCKET.ADMIN,
  UI_BUCKET.MANAGER,
  UI_BUCKET.AGENT,
  UI_BUCKET.VIEWER,
];

/**
 * lowercase client role → UI bucket.
 * Legacy agent/manager/admin kept; industrial aliases map onto the same buckets.
 */
export const CLIENT_ROLE_TO_UI_BUCKET = {
  admin: UI_BUCKET.ADMIN,
  manager: UI_BUCKET.MANAGER,
  agent: UI_BUCKET.AGENT,
  viewer: UI_BUCKET.VIEWER,
  // Industrial aliases (Keycloak seeded roles)
  salesagent: UI_BUCKET.AGENT, // HOMS
  cashier: UI_BUCKET.AGENT, // HPOS
  buyer: UI_BUCKET.MANAGER, // HPRMS
  storekeeper: UI_BUCKET.MANAGER, // HPiMS (when wired)
  accountant: UI_BUCKET.MANAGER, // ACCOUNTS (Accounts-View wired)
};

export function uiBucketFromClientRole(role) {
  if (!role) return null;
  const lower = String(role).trim().toLowerCase();
  if (lower.startsWith('manage-account')) return UI_BUCKET.ADMIN;
  return CLIENT_ROLE_TO_UI_BUCKET[lower] || null;
}

export function isMappedClientRole(role) {
  return uiBucketFromClientRole(role) != null;
}

export function resolveUiBuckets(roles = []) {
  const buckets = new Set();
  (roles || []).forEach((role) => {
    const bucket = uiBucketFromClientRole(role);
    if (bucket) buckets.add(bucket);
  });
  return buckets;
}

/** Highest-privilege UI bucket for menu / route selection. */
export function primaryUiBucket(rolesOrRole) {
  const roles = Array.isArray(rolesOrRole)
    ? rolesOrRole
    : rolesOrRole
      ? [rolesOrRole]
      : [];
  const buckets = resolveUiBuckets(roles);
  for (let i = 0; i < BUCKET_PRIORITY.length; i += 1) {
    if (buckets.has(BUCKET_PRIORITY[i])) return BUCKET_PRIORITY[i];
  }
  return null;
}

/** True when the user should see the authenticated app shell (sidebar). */
export function hasAppShellAccess(rolesOrRole) {
  return primaryUiBucket(rolesOrRole) != null;
}

export function isUiAdmin(rolesOrRole) {
  return primaryUiBucket(rolesOrRole) === UI_BUCKET.ADMIN;
}

export function isUiManager(rolesOrRole) {
  return primaryUiBucket(rolesOrRole) === UI_BUCKET.MANAGER;
}

export function isUiAgent(rolesOrRole) {
  return primaryUiBucket(rolesOrRole) === UI_BUCKET.AGENT;
}

export function isUiViewer(rolesOrRole) {
  return primaryUiBucket(rolesOrRole) === UI_BUCKET.VIEWER;
}

/** False for Viewer — hide Create / Edit / stock adjust and similar writes. */
export function canMutateUi(rolesOrRole) {
  const bucket = primaryUiBucket(rolesOrRole);
  return bucket != null && bucket !== UI_BUCKET.VIEWER;
}

/** HOMS module page keys that imply create/edit (blocked for Viewer). */
export function isMutatingHomsPageKey(pageKey) {
  const key = String(pageKey || '').toLowerCase();
  if (!key) return false;
  return (
    key.endsWith('.create') ||
    key.endsWith('.edit') ||
    key.endsWith('.add') ||
    key.endsWith('.new') ||
    key.includes('.create.') ||
    key.includes('.edit.')
  );
}

/** MenuConfig key: admin | manager | agent | viewer */
export function menuKeyFromUiBucket(bucket) {
  if (bucket === UI_BUCKET.ADMIN) return 'admin';
  if (bucket === UI_BUCKET.MANAGER) return 'manager';
  if (bucket === UI_BUCKET.AGENT) return 'agent';
  if (bucket === UI_BUCKET.VIEWER) return 'viewer';
  return null;
}
